/* eslint-disable lingui/no-unlocalized-strings */
import ArrowDown from '@/assets/arrow-down.svg?react';
import { alpha, createTheme } from '@mui/material';

import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

export const my = {
  primary: {
    '300': '#FF88C2',
    '400': '#FF4DA6',
    '500': '#F62092',
    '600': '#C51475',
    '700': '#A0105F',
  },

  neutral: {
    black: '#000000',
    '300': '#4D4D4D',
    '700': '#B3B3B3',
    '900': '#E6E6E6',
    white: '#FFFFFF',
  },

  error: '#DA4545',
  warning: '#FFCC00',
  info: '#007AFF',
  success: '#10A342',
} as const;

const breakpoints = {
  xs: 0,
  sm: 480,
  md: 800,
  lg: 1288,
  xl: 1400,
} as const;

const defaultTheme = createTheme();

type MyTheme = typeof my;

declare module '@mui/material/styles' {
  interface Theme {
    my: MyTheme;
  }
  interface DeprecatedThemeOptions {
    my: MyTheme;
  }
  interface ThemeOptions {
    my: MyTheme;
  }

  interface Palette {
    my: MyTheme;
  }

  interface PaletteOptions {
    my: MyTheme;
  }

  interface TypographyVariants {
    '32': React.CSSProperties;
    '24': React.CSSProperties;
    '20': React.CSSProperties;
    '18': React.CSSProperties;
    '16': React.CSSProperties;
    '14': React.CSSProperties;
    '12': React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    '32': React.CSSProperties;
    '24': React.CSSProperties;
    '20': React.CSSProperties;
    '18': React.CSSProperties;
    '16': React.CSSProperties;
    '14': React.CSSProperties;
    '12': React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    '32': true;
    '24': true;
    '20': true;
    '18': true;
    '16': true;
    '14': true;
    '12': true;
  }
}

const shadows = [
  'none',
  ...Array(24).fill('1px 1px 15px 0px rgba(0, 0, 0, 0.15)'),
] as any;

export const lineHeight = 5 / 4;

export const theme = createTheme({
  my,
  palette: {
    mode: 'light',
    my,
    primary: {
      ...my.primary,
      light: my.primary[400],
      main: my.primary[500],
      dark: my.primary[600],
      contrastText: my.neutral.black,
    },
    secondary: {
      main: my.neutral.black,
      contrastText: my.neutral.white,
    },
    action: {
      // active: alpha(my.neutral.black, 0.54 * 2),
      hover: alpha(my.neutral.black, 0.04 * 2),
      selected: alpha(my.neutral.black, 0.08 * 2),
      disabled: alpha(my.neutral.black, 0.26 * 2),
      disabledBackground: alpha(my.neutral.black, 0.12 * 2),
      focus: alpha(my.neutral.black, 0.12 * 2),
      hoverOpacity: 0.04 * 2,
      selectedOpacity: 0.08 * 2,
      disabledOpacity: 0.38 * 2,
      focusOpacity: 0.12 * 2,
      activatedOpacity: 0.12 * 2,
    },
    common: {
      black: my.neutral.black,
      white: my.neutral.white,
    },
    grey: {
      '300': my.neutral[300],
      '700': my.neutral[700],
      '900': my.neutral[900],
    },
    error: { main: my.error },
    warning: { main: my.warning },
    info: { main: my.info },
    success: { main: my.success },
    divider: my.neutral[700],
    text: { primary: my.neutral.black, secondary: my.neutral[300] },
    background: { default: my.neutral.white },
  },
  shape: { borderRadius: 5 },
  breakpoints: {
    values: breakpoints,
  },
  typography: {
    htmlFontSize: 16,
    fontFamily: 'Plus Jakarta Sans',
    allVariants: { lineHeight },
    body1: { fontSize: 16 },
    body2: { fontSize: 14, lineHeight: '18px' },
    '32': { lineHeight, fontSize: 32 },
    '24': { lineHeight, fontSize: 24 },
    '20': { lineHeight, fontSize: 20 },
    '18': { lineHeight, fontSize: 18 },
    '16': { lineHeight, fontSize: 16 },
    '14': { fontSize: 14, lineHeight: '18px' },
    '12': { lineHeight, fontSize: 12 },
    h1: { fontSize: 32 },
    h2: { fontSize: 24 },
    h3: { fontSize: 20 },
    h4: { fontSize: 16 },
    h5: { fontSize: 14, lineHeight: '18px' },
    h6: { fontSize: 12 },
    button: { textTransform: 'none' },
    caption: { fontSize: 12 },
  },
  shadows,
  components: {
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: {
          backgroundColor: alpha('#000', 0.22),
        },
      },
    },
    MuiDateTimeField: {
      defaultProps: {
        format: 'DD/MM/YYYY HH:mm',
      },
    },
    MuiToolbar: {
      defaultProps: {
        disableGutters: true,
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: { color: my.error },
        root: {
          fontSize: 16,
          lineHeight: 1.2,
          color: my.neutral.black,
          '&.Mui-focused, &.Mui-error': {
            color: my.neutral.black,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          border: `1px solid ${my.neutral[700]}`,
          borderRadius: 4,
          boxSizing: 'border-box',
          padding: '8px 16px',

          'label + &': {
            marginTop: 8,
          },
          [`& fieldset`]: { border: 'none' },

          '&:focus, &:hover': {
            borderColor: my.primary[500],
          },
          '&.Mui-error': {
            borderColor: my.error,
          },
          '&.Mui-disabled': {
            backgroundColor: my.neutral[700],
            pointerEvents: 'none',
            cursor: 'not-allowed',
          },
        },
        adornedStart: { paddingLeft: 16, fontSize: 14, lineHeight },
        inputAdornedStart: { paddingLeft: 0 },
        adornedEnd: { paddingRight: 16 },
        inputAdornedEnd: { paddingRight: 0, fontSize: 14, lineHeight },
        multiline: {
          padding: 0,
        },
        input: {
          padding: 0,
          fontSize: 14,
          lineHeight,
          height: 'auto',
          '&::placeholder': {
            color: my.neutral[700],
            opacity: 1,
          },
          '&[type=number]': {
            MozAppearance: 'textfield',
          },
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '&[type=password]::-ms-reveal, &[type=password]::-ms-clear': {
            opacity: 0,
          },
          // currently, i just delay background-color changed when
          // autocomplete on chrome. it should be better.
          // god bless you
          // '&:-webkit-autofill': {
          //   transitionDelay: '9999999s',
          //   transitionProperty: 'background-color, color',
          //   fontSize: 16,
          // },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginTop: 8, marginLeft: 0, fontSize: 14 },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
        IconComponent: ArrowDown,
        classes: { icon: 'arrow', iconOpen: 'open' },
        MenuProps: { disableScrollLock: true },
      },
      styleOverrides: {
        select: {
          fontSize: 14,
          lineHeight,
          padding: '8px 16px',
        },
        outlined: {
          padding: 0,
          minHeight: 'unset',
        },
      },
      // styleOverrides: { icon: { color: 'inherit' } },
    },
    MuiMenuList: {},
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: shadows[1],
        },
        list: {},
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          // fontWeight: 600,
          // padding: 16,
          // borderRadius: 10,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          '32': 'div',
          '24': 'div',
          '20': 'div',
          '18': 'div',
          '16': 'p',
          '14': 'span',
          '12': 'span',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          [defaultTheme.breakpoints.down(500)]: {
            paddingLeft: 8,
            paddingRight: 8,
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 'unset' },
      },
      defaultProps: {
        indicatorColor: 'primary',
        textColor: 'primary',
        TabIndicatorProps: {
          sx: {
            height: '3px',
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: 0,
          minHeight: 'unset',
          minWidth: 'unset',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0,0,0,0.6)',
        },
        invisible: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover', color: 'textPrimary' },
      styleOverrides: {
        root: {
          '&[role="button"]': {
            cursor: 'pointer',
          },
        },
        underlineHover: { cursor: 'pointer' },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableFocusRipple: true,
        size: 'medium',
        variant: 'contained',
      },
      styleOverrides: {
        root: { gap: 8, borderRadius: 8, fontWeight: 700, fontSize: 14 },
        // sizeSmall: {
        //   padding: '10px 8px',
        // },
        sizeMedium: {
          minHeight: 34,
          padding: '8px 16px',
        },
        sizeLarge: {
          padding: '12px 16px',
        },
        // containedPrimary: {},
        // colorPrimary: {
        //   '&:hover': {
        //     backgroundColor: my.primary[300],
        //   },
        //   '&.Mui-disabled': {
        //     backgroundColor: my.neutral[900],
        //     color: my.neutral[300],
        //   },
        // },
        containedInherit: {
          backgroundColor: my.neutral.black,
          color: my.neutral.white,
          // '&:hover': {
          //   outline: `1px solid ${my.primary[500]}`,
          // },
          // '&.Mui-disabled': {
          //   backgroundColor: my.neutral[700],
          //   color: my.neutral.white,
          // },
        },
        startIcon: { marginLeft: 0, marginRight: 0 },
        endIcon: { marginLeft: 0, marginRight: 0 },
      },
    },
    MuiIconButton: {
      defaultProps: { size: 'medium' },
    },
    MuiLoadingButton: {
      defaultProps: {
        disableElevation: true,
        disableFocusRipple: true,
        size: 'medium',
        variant: 'contained',
      },
      // it's already inherited from muibutton above
      // styleOverrides: {
      //   root: {
      //     borderRadius: 32,
      //     fontWeight: 600,
      //     textTransform: 'none',
      //     fontSize: 16,
      //     '&.Mui-disabled': {
      //       color: '#99999F',
      //     },
      //   },
      // },
    },
    MuiTooltip: {
      defaultProps: { arrow: true, placement: 'top' },
      styleOverrides: {
        arrow: {
          color: my.neutral[300],
        },
        tooltip: {
          marginBottom: '8px !important',
          backgroundColor: my.neutral[300],
          color: my.neutral[900],
        },
      },
    },
  },
});
